import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ChartBarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  StarIcon,
  CalendarIcon,
  FunnelIcon,
  ChevronDownIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { useProviderAnalytics } from '../../../hooks/useProviders'

const timeRanges = [
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
  { value: 'year', label: 'Last year' },
  { value: 'all', label: 'All time' },
]

const serviceTypes = [
  { value: '', label: 'All Services' },
  { value: 'AMBULANCE_PROVIDER', label: 'Ambulance' },
  { value: 'USTADZ', label: 'Ustadz' },
  { value: 'CEMETERY_PROVIDER', label: 'Cemetery' },
  { value: 'VENDOR', label: 'Vendor' },
  { value: 'PROVIDER', label: 'General Provider' },
]

export default function ProviderAnalyticsPage() {
  const [timeRange, setTimeRange] = useState('month')
  const [serviceType, setServiceType] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const { data: analyticsData, isLoading, error, refetch } = useProviderAnalytics(timeRange, serviceType || undefined)

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Exporting analytics data...')
  }

  if (isLoading && !analyticsData) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Provider Analytics</h1>
            <p className="text-gray-600 mt-1">Track provider performance and growth metrics</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExport}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Export Report
            </button>
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <Link
              to="/admin/providers"
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Back to Overview
            </Link>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
              <div className="relative">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="appearance-none block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  {timeRanges.map((range) => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <ChevronDownIcon className="h-4 w-4" />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Service Type</label>
              <div className="relative">
                <select
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                  className="appearance-none block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  {serviceTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <ChevronDownIcon className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Advanced Filters
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="text-center py-12">
          <div className="text-red-600 text-lg font-medium">Failed to load analytics</div>
          <p className="text-gray-500 mt-2">Please try again later</p>
        </div>
      )}

      {/* Analytics Content */}
      {!error && analyticsData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <UserGroupIcon className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {analyticsData.summary?.totalProviders || 0}
                  </p>
                  <p className="text-gray-600">Total Providers</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <ArrowTrendingUpIcon className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    +{analyticsData.summary?.newProviders || 0}
                  </p>
                  <p className="text-gray-600">New Providers</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <StarIcon className="h-8 w-8 text-yellow-500" />
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {analyticsData.summary?.averageRating?.toFixed(1) || '0.0'}
                  </p>
                  <p className="text-gray-600">Average Rating</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <ArrowTrendingUpIcon className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {analyticsData.summary?.activeProviders || 0}
                  </p>
                  <p className="text-gray-600">Active Providers</p>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Providers by Service Type */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Providers by Service Type</h3>
              <div className="space-y-4">
                {analyticsData.providersByType?.map((type) => {
                  const percentage = analyticsData.summary?.totalProviders > 0
                    ? (type.count / analyticsData.summary.totalProviders * 100).toFixed(1)
                    : '0.0'

                  return (
                    <div key={type.serviceType} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">
                          {serviceTypes.find(st => st.value === type.serviceType)?.label || type.serviceType}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600 w-12 text-right">
                          {type.count}
                        </span>
                      </div>
                    </div>
                  )
                })}
                {(!analyticsData.providersByType || analyticsData.providersByType.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    No provider data available
                  </div>
                )}
              </div>
            </div>

            {/* Top Performers */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Rated Providers</h3>
              <div className="space-y-3">
                {analyticsData.topProviders?.map((provider, index) => (
                  <div key={provider.id} className="flex items-center justify-between py-2">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-500 w-8">#{index + 1}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{provider.companyName}</p>
                        <p className="text-xs text-gray-500">
                          {serviceTypes.find(st => st.value === provider.serviceType)?.label}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                      <span className="text-sm font-medium text-gray-900">
                        {provider.rating?.toFixed(1) || '0.0'}
                      </span>
                    </div>
                  </div>
                ))}
                {(!analyticsData.topProviders || analyticsData.topProviders.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    No provider ratings available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Growth Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Provider Growth Trend</h3>
              <span className="text-sm text-gray-500">
                {timeRanges.find(tr => tr.value === timeRange)?.label}
              </span>
            </div>
            <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
              <div className="text-center">
                <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">
                  Growth chart visualization coming soon
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {analyticsData.growth?.length || 0} data points available
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}